import React, { createContext, useContext, useEffect, useMemo, Component, ErrorInfo, ReactNode } from 'react';
import { useAdminIam, type AdminIamResult } from '@/hooks/useAdminIam';

export type AdminContextValue = AdminIamResult & {
  loading: boolean;
  error: string | null;
};

const defaultAdminState: AdminContextValue = {
  isAdmin: false,
  canPerformAdminActions: false,
  isLoading: false,
  loading: false,
  userId: null,
  email: null,
  failureCount: 0,
  lastErrorCode: null,
  error: null,
  retry: () => {},
};

export const AdminContext = createContext<AdminContextValue | undefined>(undefined);

class AdminProviderErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[AdminProvider] Error boundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <AdminContext.Provider value={defaultAdminState}>
          {this.props.children}
        </AdminContext.Provider>
      );
    }

    return this.props.children;
  }
}

const AdminProviderCore: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const parentContext = useContext(AdminContext);
  const isNested = Boolean(parentContext);

  // CRITICAL: Call hooks unconditionally at the top (rules of hooks)
  // useAdminIam now returns default state instead of throwing
  const state = useAdminIam();

  const value = useMemo<AdminContextValue>(() => ({
    ...state,
    loading: state.isLoading,
    error: state.lastErrorCode,
  }), [state]);

  useEffect(() => {
    if (isNested && import.meta.env.DEV) {
      console.warn('[AdminProvider] Nested provider detected. Reusing existing context instance.');
    }
  }, [isNested]);

  // Now we can do early returns
  if (isNested) {
    return <>{children}</>;
  }

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <AdminProviderErrorBoundary>
      <AdminProviderCore>{children}</AdminProviderCore>
    </AdminProviderErrorBoundary>
  );
};

export function useAdmin(): AdminContextValue {
  const ctx = useContext(AdminContext);
  if (!ctx) {
    // Return default state instead of throwing to prevent AdminGuardErrorBoundary from catching
    // This allows RouteGuard to handle the error gracefully
    console.warn('[useAdmin] AdminContext not available, returning default state');
    return defaultAdminState;
  }
  return ctx;
}
